import { type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useAppSelector } from '@app/hooks';
import type { ConfigurablePage } from '@shared/constants/pages';
import {
  selectCurrentUser,
  selectPages,
  selectSessionStatus,
  selectSignOutReason,
} from '@shared/session/sessionSlice';

import { ForbiddenPage } from '../pages/ForbiddenPage';

/** Signed-in only; otherwise to /login?next=<where they were going>. */
export function RequireAuth() {
  const status = useAppSelector(selectSessionStatus);
  const reason = useAppSelector(selectSignOutReason);
  const location = useLocation();
  if (status !== 'authenticated') {
    // After pressing "Sign out", start fresh: the next person may not share their pages.
    if (reason === 'user') return <Navigate to="/login" replace />;
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** Forces /change-password while an admin-set or first password is in place. */
export function RequirePasswordFresh() {
  const user = useAppSelector(selectCurrentUser);
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  return <Outlet />;
}

/** Signed-in users are sent away from the sign-in screens. */
export function GuestOnly() {
  const status = useAppSelector(selectSessionStatus);
  if (status === 'authenticated') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

/** Page-level permission (mirrors the backend's require_page). */
export function RequirePage({ page, children }: { page: ConfigurablePage; children: ReactNode }) {
  const pages = useAppSelector(selectPages);
  if (!pages[page]) return <ForbiddenPage />;
  return children;
}
